/**
 * Public participant list.
 *
 * Receives its rows instead of fetching them: the share overview shows the list
 * only for a signed-in visitor, while the by-token API call that produces the
 * rows stays in the page that knows whether a session exists.
 *
 * Only what a share link may reveal is rendered here. Coordinates never reach
 * this component, which is why it takes the public DTO and not the owner one.
 */

import { useTranslation } from 'react-i18next'

import type { PublicParticipantDto } from '@shared/types'

import { activityLabelKey, cn, modeLabelKey } from '../../lib/format'
import { Badge } from '../ui/badge'
import { EmptyState } from '../ui/primitives'

export interface ParticipantListProps {
  participants: PublicParticipantDto[]
  className?: string
}

/** Formats an optional `HH:mm` availability window, or `null` when unset. */
function availabilityText(start: string | null, end: string | null): string | null {
  if (start && end) {
    return `${start} - ${end}`
  }
  return start ?? end
}

export function ParticipantList({ participants, className }: ParticipantListProps) {
  const { t } = useTranslation()

  if (participants.length === 0) {
    return <EmptyState className={className} title={t('common.empty')} />
  }

  return (
    <ul className={cn('divide-border flex flex-col divide-y', className)}>
      {participants.map((participant) => {
        const availability = availabilityText(
          participant.availableTimeStart,
          participant.availableTimeEnd,
        )

        return (
          <li key={participant.id} className="flex flex-col gap-2 py-3">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-sm font-medium">{participant.nickname}</span>
              {participant.departureLabel ? (
                <span className="text-muted-foreground text-xs">{participant.departureLabel}</span>
              ) : null}
            </div>

            <div className="flex flex-wrap gap-1.5">
              {participant.modes.map((mode) => (
                <Badge key={mode} variant="outline">
                  {t(modeLabelKey(mode))}
                </Badge>
              ))}
              {participant.attendingActivities.map((activity) => (
                <Badge key={activity} variant="secondary">
                  {t(activityLabelKey(activity))}
                </Badge>
              ))}
            </div>

            <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
              <span>
                {t('join.maxTravelShort')}:{' '}
                {t('join.maxTravelUnit', { minutes: participant.maxTravelMinutes })}
              </span>
              {availability ? (
                <span>
                  {t('join.availableWindow')}: {availability}
                </span>
              ) : null}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
